import { createContext, useCallback, useEffect, useState } from "react";
import { WP_JSON_URL } from "../../data/const";
import { cleanWordpressHTML } from "../../data/utils";

export const PostContext = createContext();

export function PostProvider({ children }) {
    const [searchableData, setSearchableData] = useState([]);
    const [featuredPosts, setFeaturedPosts] = useState([]);
    const [totalPosts, setTotalPosts] = useState(0);
    const [cache, setCache] = useState({ orderedIds: [], posts: {} }); // posts also includes specifically requested posts

    const addPostsToCache = useCallback((postsArray, currentPage, perPage) => {
        setCache(({ orderedIds, posts }) => {
            const newOrderedIds = [...orderedIds];
            const newPosts = { ...posts };

            postsArray.forEach((p, subIndex) => {
                const index = perPage * (currentPage - 1) + subIndex;
                if (!newPosts[p.id]) {
                    newOrderedIds[index] = p.id;
                    newPosts[p.id] = cleanWordpressHTML(p);
                }
            });

            return { orderedIds: newOrderedIds, posts: newPosts };
        });
    }, []);

    const getPosts = useCallback(
        async (currentPage, perPage, setLoading = () => {}) => {
            // removed this because [x, null, null, y] has length 4 and would prevent index 1, 2 from being loaded
            // if (cache.orderedIds.length < currentPage * perPage) {
            const destinationSlice = cache.orderedIds.slice(
                (currentPage - 1) * perPage,
                currentPage * perPage
            );

            if (
                !destinationSlice.every((p) => p) ||
                cache.orderedIds.length <= (currentPage - 1) * perPage
            ) {
                // at least one empty

                setLoading(true);
                await fetch(
                    `${WP_JSON_URL}posts/?page=${currentPage}&per_page=${perPage}`
                )
                    .then((res) => {
                        setTotalPosts(res.headers.get("x-wp-total"));
                        return res.json();
                    })
                    .then((posts) =>
                        addPostsToCache(posts, currentPage, perPage)
                    )
                    .finally(() => setLoading(false));
            }
        },
        [addPostsToCache, cache.orderedIds]
    );

    const getSpecificPost = useCallback(
        async (id, setLoading = () => {}, onError = () => {}) => {
            if (cache.posts[id]) {
                setLoading(false);
                return cache.posts[id];
            } else {
                setLoading(true);
                await fetch(`${WP_JSON_URL}posts/${id}`)
                    .then((res) => {
                        if (!res.ok) {
                            onError();
                        }
                        return res.json();
                    })
                    .then((post) => {
                        setCache(({ orderedIds, posts }) => {
                            const newPosts = { ...posts };
                            newPosts[id] = post;

                            return { orderedIds, posts: newPosts };
                        });
                    })
                    .finally(() => setLoading(false));
            }
        },
        [cache.posts]
    );

    const getPostsWithQuery = useCallback(
        async (
            currentPage,
            perPage,
            cat,
            query,
            setLoading = () => {},
            setTotalQueryResults
        ) => {
            setLoading(true);
            return await fetch(
                `${WP_JSON_URL}posts/search?q=${query}&cat=${cat}&page=${currentPage}&per_page=${perPage}`
            )
                .then((res) => {
                    setTotalQueryResults(res.headers.get("x-wp-total"));
                    return res.json();
                })
                .then((results) => {
                    console.log(results);
                    return cleanWordpressHTML(results);
                })
                .finally(() => setLoading(false));
        },
        []
    );

    useEffect(() => {
        if (searchableData.length === 0) {
            fetch(`${WP_JSON_URL}posts/overview`)
                .then((res) => {
                    return res.json();
                })
                .then((data) => {
                    setSearchableData(data.suggestions);
                    setFeaturedPosts(data.featured);
                });
        }
    }, [searchableData]);

    const contextObject = {
        cache,
        totalPosts,
        getPosts,
        getSpecificPost,
        searchableData,
        featuredPosts,
        getPostsWithQuery,
    };

    return (
        <PostContext.Provider value={contextObject}>
            {children}
        </PostContext.Provider>
    );
}
